import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useSubscription } from "../context/SubscriptionContext";
import LoadingScreen from "../components/LoadingScreen";
import TheoryView from "../components/TheoryView";
import QuizView from "../components/QuizView";
import ResultsView from "../components/ResultsView";
import WeakReviewView from "../components/WeakReviewView";
import { toast } from "sonner";

/**
 * Two modes:
 *  1. Fresh: topic + demo → generate theory (consumes quota)
 *  2. Replay: historySessionId → load previous theory (no quota)
 */
export default function Learn({ topic, demo, historySessionId, onExit, onQuotaExceeded }) {
  const { subscription, refresh } = useSubscription();
  const [state, setState] = useState("loading_theory");
  const [theory, setTheory] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [review, setReview] = useState(null);
  const [miniQuiz, setMiniQuiz] = useState(null);
  const [miniAnswers, setMiniAnswers] = useState([]);
  const requested = useRef(false);

  useEffect(() => {
    if (requested.current) return;
    requested.current = true;
    (async () => {
      try {
        if (historySessionId) {
          const { data } = await api.get(`/history/${historySessionId}`);
          setTheory(data.theory);
          setSessionId(data.session_id);
          if (data.quiz && Array.isArray(data.answers)) {
            setQuiz(data.quiz);
            setAnswers(data.answers);
            setState("results");
          } else {
            setState("theory");
          }
          return;
        }
        const { data } = await api.post("/generate-theory", { topic, demo });
        setTheory(data);
        if (data._session_id) setSessionId(data._session_id);
        if (!demo) await refresh();
        setState("theory");
      } catch (e) {
        if (e.response?.status === 402) {
          onQuotaExceeded();
          return;
        }
        toast.error("Nie udało się wczytać materiału.");
        setState("theory_error");
      }
    })();
  }, []); // run once

  const startQuiz = async () => {
    setState("loading_quiz");
    try {
      const { data } = await api.post("/generate-quiz", { topic: topic || theory?.topic || "temat", theory, n: 10, demo });
      setQuiz(data);
      setAnswers([]);
      setState("quiz");
    } catch {
      toast.error("Nie udało się wygenerować quizu.");
      setState("theory");
    }
  };

  const finishQuiz = async (finalAnswers) => {
    // FREE plan: user can read theory + take all 10 quiz questions,
    // but results are gated behind a subscription.
    if (!demo && subscription?.plan === "free") {
      onQuotaExceeded();
      return;
    }
    setAnswers(finalAnswers);
    setState("results");
    // Save result to history (best-effort, silently ignore errors)
    if (sessionId && !demo) {
      try {
        await api.post("/history/save-result", {
          session_id: sessionId, quiz, answers: finalAnswers,
        });
      } catch { /* ignore */ }
    }
  };

  const repeatWeak = async (wrongQuestions) => {
    setState("loading_weak");
    try {
      const { data } = await api.post("/generate-weak-review", {
        topic: topic || theory?.topic || "temat",
        wrong_questions: wrongQuestions,
        demo,
      });
      setReview(data);
      setState("weak_review");
    } catch {
      toast.error("Nie udało się wygenerować powtórki.");
      setState("results");
    }
  };

  const startMiniQuiz = () => {
    setMiniQuiz({ questions: review.questions });
    setMiniAnswers([]);
    setState("mini_quiz");
  };

  const finishMiniQuiz = (a) => {
    setMiniAnswers(a);
    setState("mini_results");
  };

  if (state === "loading_theory") return <LoadingScreen label={historySessionId ? "Wczytuję notatki..." : "Przygotowuję materiał..."} />;
  if (state === "loading_quiz") return <LoadingScreen label="Tworzę quiz..." />;
  if (state === "loading_weak") return <LoadingScreen label="Przygotowuję powtórkę..." />;

  if (state === "theory" || state === "theory_error") {
    if (!theory) return <LoadingScreen label="Przygotowuję materiał..." />;
    return <TheoryView theory={theory} onStartQuiz={startQuiz} onBack={onExit} />;
  }

  if (state === "quiz") return <QuizView questions={quiz.questions} title="Quiz" onFinish={finishQuiz} />;
  if (state === "results")
    return (
      <ResultsView
        questions={quiz.questions}
        answers={answers}
        onRepeatWeak={repeatWeak}
        onNewTopic={onExit}
      />
    );

  if (state === "weak_review") return <WeakReviewView review={review} onStartMiniQuiz={startMiniQuiz} />;
  if (state === "mini_quiz")
    return <QuizView questions={miniQuiz.questions} title="Mini quiz (5 pytań)" onFinish={finishMiniQuiz} />;
  if (state === "mini_results")
    return (
      <ResultsView
        questions={miniQuiz.questions}
        answers={miniAnswers}
        onRepeatWeak={repeatWeak}
        onNewTopic={onExit}
        subtitle="Wyniki powtórki"
      />
    );

  return null;
}
